import { Box, Divider, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PlayerPosition as PlayerPositionType } from "../../../../../types/types/user.types";

import PlayingField from "../../../assets/images/playing-field.png";

const positionStyles: { [key in PlayerPositionType]: React.CSSProperties[] } = {
  "GOALKEEPER (GK)": [{ top: "50%", left: "2.18%" }],
  "LEFT BACK (LB)": [{ top: "10%", left: "25%" }],
  "CENTER BACK (CB)": [
    { top: "30%", left: "20%" },
    { top: "70%", left: "20%" },
  ],
  "SWEEPER (SW)": [{ top: "50%", left: "17%" }],
  "RIGHT BACK (RB)": [{ top: "90%", left: "25%" }],
  "LEFT MIDFIELDER (LMF)": [{ top: "10%", left: "48%" }],
  "CENTER MIDFIELDER (CMF)": [
    { top: "30%", left: "43%" },
    { top: "70%", left: "43%" },
  ],
  "DEFENSIVE MIDFIELDER (DMF)": [{ top: "50%", left: "35%" }],
  "ATTACKING MIDFIELDER (AMF)": [{ top: "50%", left: "65%" }],
  "RIGHT MIDFIELDER (RMF)": [{ top: "90%", left: "48%" }],
  "LEFT WINGER (LW)": [{ top: "10%", left: "65%" }],
  "RIGHT WINGER (RW)": [{ top: "90%", left: "65%" }],
  "STRIKER (ST)": [{ top: "30%", left: "80%" }],
  "CENTER FORWARD (CF)": [{ top: "70%", left: "80%" }],
};

const positionShorthands: { [key in PlayerPositionType]: string } = {
  "GOALKEEPER (GK)": "GK",
  "LEFT BACK (LB)": "LB",
  "CENTER BACK (CB)": "CB",
  "SWEEPER (SW)": "SW",
  "RIGHT BACK (RB)": "RB",
  "LEFT MIDFIELDER (LMF)": "LMF",
  "CENTER MIDFIELDER (CMF)": "CMF",
  "DEFENSIVE MIDFIELDER (DMF)": "DMF",
  "ATTACKING MIDFIELDER (AMF)": "AMF",
  "RIGHT MIDFIELDER (RMF)": "RMF",
  "LEFT WINGER (LW)": "LW",
  "RIGHT WINGER (RW)": "RW",
  "STRIKER (ST)": "ST",
  "CENTER FORWARD (CF)": "CF",
};

const PlayerPosition = ({
  mainPosition,
  secondaryPosition,
  isLoading,
}: {
  mainPosition?: PlayerPositionType;
  secondaryPosition?: PlayerPositionType;
  isLoading: boolean;
}) => {
  const highlighted = {
    main: false,
    secondary: false,
  };
  const { t } = useTranslation("profiles", { keyPrefix: "overview" });
  const { t: tCommon } = useTranslation("common", { keyPrefix: "user.playerPosition" });
  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("playerPosition")}</Typography>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.infoContainer}>
          <Box sx={styles.positionContainer}>
            <Typography sx={{ ...styles.positionLabel, ...styles.primaryDot }}>{t("primary")}</Typography>
            <Typography sx={styles.positionText}>
              {isLoading && <Skeleton variant="text" width="50%" />}
              {!isLoading && mainPosition && tCommon(mainPosition)}
            </Typography>
          </Box>
          <Divider orientation="horizontal" flexItem sx={styles.divider} />
          <Box sx={styles.positionContainer}>
            <Typography sx={{ ...styles.positionLabel, ...styles.secondaryDot }}>{t("other")}</Typography>
            <Typography sx={styles.positionText}>
              {isLoading && <Skeleton variant="text" width="50%" />}
              {!isLoading ? (secondaryPosition ? tCommon(secondaryPosition) : "--") : null}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.fieldContainer}>
          {isLoading ? (
            <Skeleton variant="rectangular" width="360px" height="225px" sx={styles.fieldSkeleton} />
          ) : (
            <>
              <img src={PlayingField} alt="Playing Field" style={styles.fieldImage} />
              {Object.entries(positionStyles).map(([position, positionStyle]) => {
                return positionStyle.map((style, index) => {
                  const isMain = position === mainPosition && !highlighted.main;
                  const isSecondary = position === secondaryPosition && !highlighted.secondary;

                  if (isMain) highlighted.main = true;
                  if (isSecondary) highlighted.secondary = true;

                  return (
                    <span
                      key={`${position}-${index}`}
                      style={
                        {
                          ...style,
                          ...styles.positionMarker(isMain, isSecondary),
                        } as React.CSSProperties
                      }
                    >
                      {positionShorthands[position as PlayerPositionType]}
                    </span>
                  );
                });
              })}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlayerPosition;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: {
      xs: "column",
      sm: "row",
    },
    alignItems: {
      xs: "flex-start",
      sm: "center",
    },
    gap: {
      xs: "16px",
      sm: "0",
    },
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "19px",
    flex: 1,
    width: {
      xs: "100%",
      sm: "auto",
    },
  },
  positionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
  },
  positionLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    paddingLeft: "16px",
    position: "relative",
  },
  primaryDot: {
    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "8px",
      height: "8px",
      borderRadius: "2px",
      backgroundColor: "#8FFF3C",
    },
  },
  secondaryDot: {
    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "8px",
      height: "8px",
      borderRadius: "2px",
      backgroundColor: "#F9B223",
    },
  },
  positionText: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  divider: {
    backgroundColor: "var(--gray-200, #EAECF0)",
  },
  fieldContainer: {
    position: "relative",
    display: "inline-block",
    padding: 0,
    margin: 0,
    width: "360px",
    height: "225px",
  },
  fieldImage: {
    width: "360px",
    height: "225px",
    objectFit: "cover",
  },
  fieldSkeleton: {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    borderRadius: "8px",
  },
  positionMarker: (isMain: boolean, isSecondary: boolean) => ({
    position: "absolute",
    cursor: "default",
    color: isMain || isSecondary ? "var(--Dark-1000, #111)" : "#FFF",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "12px",
    padding: "2px 10px",
    borderRadius: "4px",
    border: isMain || isSecondary ? "none" : "1px solid #00E606",
    background: isMain ? "#8FFF3C" : isSecondary ? "#F9B223" : "rgba(255, 255, 255, 0.10)",
    fontFamily: "Montserrat",
    transform: "translateY(-50%)",
  }),
} as const;
